import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { HttpStatusCode } from "axios";
import {
  getLocalValue,
  removeLocalValue,
  setLocalValue,
} from "../../../libs/localStorage";
import { reissueToken } from "../../../libs/request";

import {
  deleteLongTermToken,
  getLongTermToken,
} from "../../../services/authServices";
import { useUserContext } from "../../../contexts/userContext";

import useSimplePopup from "../../../hooks/useSimplePopup";
import useBioMetrics from "../../../hooks/useBioMetrics";
import useFullScreenLoading from "../../../hooks/useFullScreenLoading";

import StickyHeader from "../../../components/Layout/StickyHeader";
import HorizontalRule from "../../../components/Rule/Horizontal";
import CommonButton from "../../../components/Button";

import faceAuthIcon from "../../../assets/img/faceAuth.png";
import fingerPrintIcon from "../../../assets/img/fingerPrintIcon.png";

type Props = {};

export default function ConfigBiometricAuthentication(props: Props) {
  const navigate = useNavigate();
  const [result, bioCall] = useBioMetrics();
  const [isLoading, setIsLoading] = React.useState(false);
  const loadingComponent = useFullScreenLoading({
    isLoading: (result?.isLoading ?? false) || isLoading,
  });
  const [hasBioToken, setHasBioToken] = React.useState<boolean>(
    getLocalValue("BIO") ? true : false
  );
  const [user] = useUserContext();
  const [simplePopupComponent, { open }] = useSimplePopup({});

  const setBioToken = async (value: boolean) => {
    if (value) {
      const jwtToken = getLocalValue("jwtToken");
      if (!jwtToken) return console.log("jwtToken is null");

      let response;
      try {
        response = await reissueToken(JSON.parse(jwtToken));
        if (response.status !== HttpStatusCode.Ok) {
          open("토큰 재발급에 실패했습니다.");
          return false;
        } else {
          // nothing to do
        }
      } catch (e: any) {
        if (e?.response?.data?.error) {
          open(e.response.data.error?.replace(". ", ".\n"));
        } else {
          open("토큰 재발급에 실패했습니다.");
        }
        return false;
      }

      try {
        const res = await getLongTermToken(
          {
            accessToken: `Bearer ${response?.data.accessToken}`,
          },
          { scope: "BIO" }
        );
        // setError((prev) => prev + " 3." + JSON.stringify(res));
        if (res.status !== HttpStatusCode.Ok) {
          open("생체 인증 발급에 실패했습니다.");
          return false;
        } else {
          setLocalValue("BIO", res.data.longTermToken);
          setLocalValue("bioUser", user.email as string);
          open("생체 인증이 연결되었습니다.", {
            onClick: () => {
              navigate("/myinfo");
            },
          });
        }
      } catch (e: any) {
        if (e?.response?.data?.error) {
          open(e.response.data.error?.replace(". ", ".\n"));
        } else {
          open("생체 인증 발급에 실패했습니다.");
          return false;
        }
      }
      return true;
    } else {
      removeLocalValue("BIO");
      return true;
    }
  };

  useEffect(() => {
    if (result.isLoading === false) {
      if (result.result === true) {
        setBioToken(true)
          .then((res) => {
            if (res) {
              setHasBioToken(true);
            }
          })
          .finally(() => setIsLoading(false));
      } else if (result.result === false) {
        open("생체 인증에 실패했습니다.");
        setIsLoading(false);
      } else {
        // nothing to do
      }
    }
  }, [result.isLoading, result.result]);

  return (
    <div>
      {loadingComponent}
      <div className="px-5">
        <StickyHeader
          title="생체인증 로그인 설정하기"
          onBackClick={() => navigate("/myinfo")}
        />
        <div className="flex flex-col gap-2.5">
          <div className=" text-3xl whitespace-pre-line font-normal pt-5">
            <strong>생체 인증</strong>
            {"으로\n편리하게 이용해보세요"}
          </div>
          <div className=" text-brand-neutral-50 text-base">
            생체인증 로그인을 등록합니다.
          </div>
        </div>
        <HorizontalRule className="bg-black !mt-[33px]" />
        <div className="flex justify-between px-2.5">
          <div className="flex items-center gap-2.5 text-lg font-medium">
            <img
              src={fingerPrintIcon}
              alt="fingerPrintIcon"
              className="w-[38px]"
            />
            <img src={faceAuthIcon} alt="faceAuthIcon" className="w-[38px]" />
          </div>
          {hasBioToken ? (
            <CommonButton
              variant="info"
              className="text-xs !p-2.5"
              onClick={async () => {
                try {
                  await deleteLongTermToken({
                    Scope: "BIO",
                    Token: getLocalValue("BIO") ?? "",
                  });
                } catch (e: any) {
                  if (e?.response?.data?.error) {
                    open(e.response.data.error?.replace(". ", ".\n"));
                  } else {
                    open("생체 인증 연결 해제에 실패했습니다.");
                    return false;
                  }
                }
                removeLocalValue("BIO");
                setHasBioToken(false);
                navigate("/myinfo");
              }}
            >
              연결끊기
            </CommonButton>
          ) : (
            <CommonButton
              variant="info"
              className="text-xs !p-2.5"
              onClick={() => {
                setIsLoading(true);
                bioCall();
              }}
            >
              연결하기
            </CommonButton>
          )}
        </div>
        <HorizontalRule />
        <div className=" whitespace-pre-line text-center p-5 bg-[#f7f7f7]">
          {
            "생체인증 등록은 1개의 아이디로\n1개의 모바일 기기만 설정 가능합니다.\n인증정보가 변경된 경우,\n연결 해제 후 다시 연결해주세요."
          }
        </div>
      </div>

      {simplePopupComponent}
    </div>
  );
}
